import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../@types';
import type {
  CreateGoldGamesCampaign,
  GoldGamesAwardResult,
  GoldGamesBrand,
  GoldGamesCampaign,
  GoldGamesGame,
  GoldGamesFreeSpinQuery,
  GoldGamesFreeSpinRow,
} from '../@types/goldGames';
import { QUERY_KEYS } from '../constants';
import API_ROUTES from '../constants/apiRoutes';
import { getData, patchData, postData } from '../utils/api';

const route = API_ROUTES.CASINO.GOLD_GAMES_CAMPAIGNS;
const key = QUERY_KEYS.casinoGoldGamesCampaigns;

export const buildGoldGamesFreeSpinQuery = ({
  page,
  limit,
  search,
  campaign,
  playerId,
  dateFrom,
  dateTo,
}: GoldGamesFreeSpinQuery) => ({
  limit,
  ...(search ? { search } : {}),
  ...(campaign ? { campaign } : {}),
  ...(playerId ? { playerId } : {}),
  offset: (page - 1) * limit,
  dateFrom: new Date(dateFrom).toISOString(),
  dateTo: new Date(dateTo).toISOString(),
});

export const useGoldGamesFreeSpins = (marketplaceId: string, query: GoldGamesFreeSpinQuery) =>
  useQuery({
    queryKey: [key, marketplaceId, 'free-spins', query],
    queryFn: () =>
      getData<{ items: GoldGamesFreeSpinRow[]; count: number }>(
        `${route}/free-spins`,
        buildGoldGamesFreeSpinQuery(query),
        'casino'
      ),
    enabled: !!marketplaceId,
  });

export const useGoldGamesCampaign = (marketplaceId: string, recordId: string) =>
  useQuery({
    queryKey: [key, marketplaceId, 'detail', recordId],
    queryFn: () => getData<GoldGamesCampaign>(`${route}/${recordId}`, undefined, 'casino'),
    enabled: !!marketplaceId && !!recordId,
  });

export const useGoldGamesBrands = (marketplaceId: string) =>
  useQuery({
    queryKey: [key, marketplaceId, 'brands'],
    queryFn: () => getData<{ brands: GoldGamesBrand[] }>(`${route}/brands`, undefined, 'casino'),
    enabled: !!marketplaceId,
  });

export const useGoldGamesGames = (marketplaceId: string, brandId: string, currency: string) =>
  useQuery({
    queryKey: [key, marketplaceId, 'games', brandId, currency],
    queryFn: () => getData<{ games: GoldGamesGame[] }>(`${route}/brands/${brandId}/games`, { currency }, 'casino'),
    enabled: !!marketplaceId && !!brandId && !!currency,
  });

// Retry participation only on the existing campaign; never recreate it after a partial failure.
export const participateGoldGamesPlayers = async (
  campaignId: number,
  playerIds: string[]
): Promise<GoldGamesAwardResult> => {
  const failedPlayerIds: string[] = [];
  const ids = [...new Set(playerIds)];
  for (const userId of ids) {
    try {
      await postData(`${route}/${campaignId}/participate`, { userId }, 'casino');
    } catch {
      failedPlayerIds.push(userId);
    }
  }
  return { campaignId, awarded: ids.length - failedPlayerIds.length, failedPlayerIds };
};

export const awardGoldGamesPlayers = async (data: CreateGoldGamesCampaign): Promise<GoldGamesAwardResult> => {
  return postData<GoldGamesAwardResult>(route, data, 'casino');
};

export const useAwardGoldGames = () => {
  const client = useQueryClient();
  return useMutation<GoldGamesAwardResult, ApiError, CreateGoldGamesCampaign>({
    mutationFn: awardGoldGamesPlayers,
    retry: false,
    onSettled: () => client.invalidateQueries({ queryKey: [key] }),
  });
};

export const useParticipateGoldGames = () => {
  const client = useQueryClient();
  return useMutation<GoldGamesAwardResult, ApiError, { campaignId: number; playerIds: string[] }>({
    mutationFn: ({ campaignId, playerIds }) => participateGoldGamesPlayers(campaignId, playerIds),
    retry: false,
    onSettled: () => client.invalidateQueries({ queryKey: [key] }),
  });
};

export const useCancelGoldGamesCampaign = () => {
  const client = useQueryClient();
  return useMutation<unknown, ApiError, number>({
    mutationFn: (campaignId) => patchData(`${route}/${campaignId}/cancel`, {}, 'casino'),
    retry: false,
    onSettled: () => client.invalidateQueries({ queryKey: [key] }),
  });
};
